import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner
} from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';

const AddAppointment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedPatientId = queryParams.get('patientId');
  
  const [formData, setFormData] = useState({
    patientId: preselectedPatientId || '',
    doctorId: '',
    appointmentDate: new Date().toISOString().split('T')[0],
    appointmentTime: '',
    status: 'scheduled',
    appointmentType: 'regular',
    notes: '',
    roomNumber: ''
  });
  
  const [timeSelection, setTimeSelection] = useState({
    hour: '',
    minute: '',
    period: 'AM'
  });
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  const { 
    patientId, 
    doctorId, 
    appointmentDate, 
    appointmentTime, 
    status, 
    appointmentType,
    notes,
    roomNumber
  } = formData;
  
  // Load doctors and patients
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get active doctors
        const doctorsRes = await axios.get('/api/doctors.php?active=1');
        setDoctors(doctorsRes.data);
        
        // Get patients
        const patientsRes = await axios.get('/api/patients.php');
        setPatients(patientsRes.data);
        setFilteredPatients(patientsRes.data);
        
        // If patientId is provided in URL, get patient details
        if (preselectedPatientId) {
          const patient = patientsRes.data.find(p => p.id.toString() === preselectedPatientId);
          if (patient) {
            setSelectedPatient(patient);
            setSearchQuery(patient.name); // Set search query to show the patient in filtered list
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load required data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [preselectedPatientId]);
  
  // Filter patients when search query changes
  useEffect(() => {
    if (patients.length === 0) return;
    
    if (searchQuery.trim() === '') {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(
        patient =>
          patient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (patient.patientId && patient.patientId.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (patient.contactNumber && patient.contactNumber.includes(searchQuery))
      );
      setFilteredPatients(filtered);
    }
  }, [searchQuery, patients]);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    
    // If patient is selected, update selectedPatient
    if (e.target.name === 'patientId' && e.target.value) {
      const patient = patients.find(p => p.id.toString() === e.target.value);
      setSelectedPatient(patient || null);
    }
  };
  
  const onTimeChange = (field, value) => {
    const newTimeSelection = { ...timeSelection, [field]: value };
    setTimeSelection(newTimeSelection);
    
    // Convert to 24-hour format if all fields are filled
    if (newTimeSelection.hour && newTimeSelection.minute) {
      let hour24 = parseInt(newTimeSelection.hour);
      if (newTimeSelection.period === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (newTimeSelection.period === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      const time24 = `${hour24.toString().padStart(2, '0')}:${newTimeSelection.minute}`;
      setFormData({ ...formData, appointmentTime: time24 });
    }
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      // Validate required fields
      if (!patientId || !doctorId || !appointmentDate || !appointmentTime) {
        setError('Please fill all required fields');
        setSubmitting(false);
        return;
      }
      
      // Check if the doctor is available on this date (working day)
      const doctorRes = await axios.get(`/api/doctors.php?id=${doctorId}`);
      const doctor = doctorRes.data;
      
      const appointmentDay = new Date(appointmentDate).toLocaleDateString('en-US', { weekday: 'long' });
      
      // Parse availability if it's a string
      let availability = doctor.availability;
      if (typeof availability === 'string') {
        try {
          availability = JSON.parse(availability);
        } catch (e) {
          availability = { days: [], timeFrom: '09:00', timeTo: '17:00' };
        }
      }
      
      if (availability && availability.days && !availability.days.includes(appointmentDay)) {
        setError(`Dr. ${doctor.name} is not available on ${appointmentDay}s`);
        setSubmitting(false);
        return;
      }
      
      // Check if appointment time is within doctor's working hours
      if (availability && availability.timeFrom && availability.timeTo) {
        const { timeFrom, timeTo } = availability;
        if (appointmentTime < timeFrom || appointmentTime > timeTo) {
          setError(`Dr. ${doctor.name}'s working hours are from ${timeFrom} to ${timeTo}`);
          setSubmitting(false);
          return;
        }
      }
      
      const res = await axios.post('/api/appointments.php', formData);
      
      setSubmitting(false);
      
      // If coming from patient detail page, return there
      if (preselectedPatientId) {
        navigate(`/patients/${preselectedPatientId}`);
      } else {
        navigate('/appointments');
      }
    } catch (err) {
      console.error('Error creating appointment:', err);
      setError(err.response?.data?.msg || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };
  
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading appointment data...</p>
      </div>
    );
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Add New Appointment</h2>
        <Link to={preselectedPatientId ? `/patients/${preselectedPatientId}` : "/appointments"}>
          <Button variant="outline-secondary">
            <FaTimes className="me-2" /> Cancel
          </Button>
        </Link>
      </div>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {selectedPatient && (
            <Alert variant="info" className="mb-4">
              <strong>Scheduling appointment for:</strong> {selectedPatient.name} ({selectedPatient.age}, {selectedPatient.gender})
              {selectedPatient.patientId && <div>Patient ID: {selectedPatient.patientId}</div>}
            </Alert>
          )}
          
          <Form onSubmit={onSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Search Patient</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by name, ID or contact"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Select Patient *</Form.Label>
                  <Form.Select
                    name="patientId"
                    value={patientId}
                    onChange={onChange}
                    required
                    size="lg"
                    className="mb-3"
                  >
                    <option value="">Select Patient...</option>
                    {filteredPatients.map(patient => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name} - {patient.patientId || 'No ID'} 
                        ({patient.age}, {patient.gender})
                      </option>
                    ))}
                  </Form.Select>
                  <div className="d-grid">
                    <Link to="/patients/add" className="btn btn-outline-primary">
                      + Add New Patient
                    </Link>
                  </div>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Doctor *</Form.Label>
                  <Form.Select
                    name="doctorId"
                    value={doctorId}
                    onChange={onChange}
                    required
                  >
                    <option value="">Select Doctor...</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        Dr. {doctor.name} - {doctor.specialization}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Appointment Date *</Form.Label>
                      <Form.Control
                        type="date"
                        name="appointmentDate"
                        value={appointmentDate}
                        onChange={onChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Label>Appointment Time *</Form.Label>
                    <Row>
                      <Col xs={4}>
                        <Form.Group className="mb-3">
                          <Form.Select
                            value={timeSelection.hour}
                            onChange={(e) => onTimeChange('hour', e.target.value)}
                            required
                          >
                            <option value="">Hour</option>
                            {hours.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={4}>
                        <Form.Group className="mb-3">
                          <Form.Select
                            value={timeSelection.minute}
                            onChange={(e) => onTimeChange('minute', e.target.value)}
                            required
                          >
                            <option value="">Min</option>
                            {minutes.map(m => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={4}>
                        <Form.Group className="mb-3">
                          <Form.Select
                            value={timeSelection.period}
                            onChange={(e) => onTimeChange('period', e.target.value)}
                            required
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
            
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Type</Form.Label>
                  <Form.Select
                    name="appointmentType"
                    value={appointmentType}
                    onChange={onChange}
                  >
                    <option value="regular">Regular</option>
                    <option value="followup">Follow-up</option>
                    <option value="emergency">Emergency</option>
                    <option value="walkin">Walk-in</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={status}
                    onChange={onChange}
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="confirmed">Confirmed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Room Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="roomNumber"
                    value={roomNumber}
                    onChange={onChange}
                    placeholder="Enter room number (optional)"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-4">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={notes}
                onChange={onChange}
                placeholder="Add any notes about this appointment"
              />
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button
                variant="primary"
                type="submit"
                disabled={submitting}
                className="px-4"
              >
                {submitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" /> Save Appointment
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddAppointment; 

